import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

// components
import { shippingModal as messages } from '../shippingMessages';

// styles
import styles from './ShippingDetailsQuotes.scss';

// helpers
import get from 'lodash.get';
import classnames from 'classnames';
import { getShippingPrice, getFilteredQuotes } from '../shippingHelpers';

// constants
const cellClass = classnames(styles.quotesTableBodyCell);
const priceCellClass = classnames(styles.quotesTableBodyCell, styles.priceData);

const slugify = string =>
    string
        .trim()
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/([^a-zA-Z0-9\._-]+)/, '');

export function ShippingDetailsQuotesTableRowComponent(props) {
    const { currency, shipmentQuotes, relevantShippingInfo } = props;
    const hasCustomQuote = get(relevantShippingInfo, 'shipmentQuote.isCustom');
    const customQuote = get(relevantShippingInfo, 'shipmentQuote');
    const sortedShipmentQuotes = shipmentQuotes
        ? getFilteredQuotes(shipmentQuotes).sort((a, b) => {
              return a.buyerSubTotalAmount.amount - b.buyerSubTotalAmount.amount;
          })
        : null;

    if (hasCustomQuote && !sortedShipmentQuotes) {
        const customRegion = get(
            relevantShippingInfo,
            'shipment.shippingAddress.displayCityStateCountry'
        );
        return (
            <tr
                data-tn={`quote-region${customRegion ? '-' + slugify(customRegion) : ''}`}
                key={customQuote.serviceId}
            >
                <td data-tn="ship-destination" className={cellClass}>
                    {customRegion}
                </td>
                <td data-tn="ship-method-value" className={cellClass}>
                    {messages.serviceMethod(customQuote)}
                </td>
                <td data-tn="ship-time-value" className={cellClass}>
                    {customQuote.handlingTimeDisplay}
                </td>
                <td data-tn="ship-price-value" className={priceCellClass}>
                    {getShippingPrice({
                        currency,
                        convertedAmountList: get(
                            customQuote,
                            'buyerSubTotalAmount.convertedAmountList'
                        ),
                    })}
                </td>
            </tr>
        );
    }

    return sortedShipmentQuotes.map((quote = {}) => {
        const regionName = get(quote, 'serviceMethod.region.regionName');
        return (
            <tr
                data-tn={`quote-region${regionName ? '-' + slugify(regionName) : ''}`}
                key={quote.serviceId}
            >
                <td data-tn="ship-destination" className={cellClass}>
                    {regionName}
                </td>
                <td data-tn="ship-method-value" className={cellClass}>
                    {messages.serviceMethod(quote)}
                </td>
                <td data-tn="ship-time-value" className={cellClass}>
                    {quote.handlingTimeDisplay}
                </td>
                <td data-tn="ship-price-value" className={priceCellClass}>
                    {getShippingPrice({
                        currency,
                        convertedAmountList: get(quote, 'buyerSubTotalAmount.convertedAmountList'),
                    })}
                </td>
            </tr>
        );
    });
}

ShippingDetailsQuotesTableRowComponent.propTypes = {
    currency: PropTypes.string,
    shipmentQuotes: PropTypes.array,
    relevantShippingInfo: PropTypes.object,
};

export const ShippingDetailsQuotesTableRow = createFragmentContainer(
    ShippingDetailsQuotesTableRowComponent,
    {
        shipmentQuotes: graphql`
            fragment ShippingDetailsQuotesTableRow_shipmentQuotes on ShipmentQuote
                @relay(plural: true) {
                serviceId
                is1stdibsBilledQuote
                is1stdibsWhiteGloveQuote
                handlingTimeDisplay
                totalAmount {
                    currency
                    amount
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                buyerServiceMethodDisplay
                handlingTime {
                    numberOfUnits
                }
                serviceMethod {
                    serviceType
                    serviceMethodType
                    region {
                        code
                        regionName
                        regionType
                    }
                    windowMin {
                        numberOfDays: numberOfUnits
                    }
                }
                buyerSubTotalAmount {
                    amount
                    convertedAmountList {
                        currency
                        amount
                    }
                }
            }
        `,
        relevantShippingInfo: graphql`
            fragment ShippingDetailsQuotesTableRow_relevantShippingInfo on QueryRelevantShippingInfo {
                shipment {
                    shippingAddress {
                        displayCityStateCountry
                    }
                }
                shipmentQuote {
                    isCustom
                    serviceId
                    is1stdibsBilledQuote
                    is1stdibsWhiteGloveQuote
                    handlingTimeDisplay
                    totalAmount {
                        currency
                        amount
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    buyerServiceMethodDisplay
                    handlingTime {
                        numberOfUnits
                    }
                    serviceMethod {
                        serviceType
                        serviceMethodType
                        region {
                            code
                            regionName
                            regionType
                        }
                        windowMin {
                            numberOfDays: numberOfUnits
                        }
                    }
                    buyerSubTotalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                }
            }
        `,
    }
);
